import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator as CalcIcon, Coins, FileSignature, CalendarClock, Percent } from "lucide-react";
import { Reveal, SectionHeading, Accent } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import { SalaryCalc } from "@/components/calculators/SalaryCalc";
import { DividendCalc } from "@/components/calculators/DividendCalc";
import { CivilContractCalc } from "@/components/calculators/CivilContractCalc";
import { TenureCalc } from "@/components/calculators/TenureCalc";
import { VatCalc } from "@/components/calculators/VatCalc";

const TABS = [
  { id: "salary", icon: CalcIcon, Component: SalaryCalc },
  { id: "vat", icon: Percent, Component: VatCalc },
  { id: "dividend", icon: Coins, Component: DividendCalc },
  { id: "civil", icon: FileSignature, Component: CivilContractCalc },
  { id: "tenure", icon: CalendarClock, Component: TenureCalc },
];

export const Calculator = () => {
  const [tab, setTab] = useState("salary");
  const { t } = useLang();
  const active = TABS.find((x) => x.id === tab);

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-coal/40 border-y border-line py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading kicker={t.calc.kicker} title={<Accent parts={t.calc.title} />} testid="calculator-heading" />

        <Reveal>
          <div className="flex flex-wrap gap-2 mb-10" role="tablist" data-testid="calc-tabs">
            {TABS.map((x) => (
              <button
                key={x.id}
                role="tab"
                aria-selected={tab === x.id}
                data-testid={`calc-tab-${x.id}`}
                onClick={() => setTab(x.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide border transition-colors duration-300 ${
                  tab === x.id ? "border-gold bg-gold text-ink" : "border-line text-dim hover:text-mist hover:border-mist/40"
                }`}
              >
                <x.icon size={16} />
                {t.calc.tabs[x.id]}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            data-testid={`calc-panel-${tab}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="grid lg:grid-cols-2 gap-10 lg:gap-16"
          >
            <active.Component />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
